import { useMemo, useState, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FilterGroupingEnum,
  Intervention,
  InterventionFilterInputObject,
  useAssetsSearchListLazyQuery,
  useSitesSearchListLazyQuery,
} from 'graphql-common';
import ModalTableMultiselect, {
  useModalTableAllTabManager,
} from '@lib/components/ModalTableMultiselect/ModalTableMultiselect';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import useModalTableModeManager from '@lib/components/ModalTableMultiselect/hooks/useModalTableModeManager';
import useSelectedFormFieldIds from '@lib/components/ModalTableMultiselect/hooks/useSelectedFormFieldIds';
import { ModalTableMultiselectMode } from '@lib/components/ModalTableMultiselect/enums';
import { getSitesLoadOptions as getSitesLoadOptionsFn } from 'utils/fetch/searchLists/sitesSearchList';
import { getAssetsLoadOptions as getAssetsLoadOptionsFn } from 'utils/fetch/searchLists/assetsSearchList';
import useInterventionsMultiselectQueryHook from 'utils/fetch/hooks/useInterventionsMultiselectQueryHook';
import { GetLoadOptions } from '@lib/components/Select/types';
import { getLoadOptionsWithSelectedFilter } from '@lib/components/SelectPanel/utils/getLoadOptionsWithSelectedFilter';
import useFilterFields, {
  FieldNames as FilterFieldNames,
} from './utils/getFilterFields';
import getInterventionsColumns from './utils/getInterventionsColumns';
import { createAdditionalFilters } from './utils/createAdditionalFilters';

// InterventionsModalTableMultiselect intentionally does NOT expose a `mode`
// prop, nor a SelectedOnly variant. InterventionFilterInputObject has no
// `id`-IN filter — the Selected tab fetches via the `departmentsId` backward
// link (mirrors FormTemplatesModalTableMultiselect's `documentationsId`) when
// editing an existing department, or falls back to filtering the All-tab
// cache by selected ids in create flow (no departmentId yet).

type InterventionsModalTableMultiselectProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (values: string[]) => void;
  formRef: FormRefType;
  fieldName?: string;
  // When editing a department, the Selected tab fetches interventions linked
  // to this department via the `departmentsId` filter. Without an id (create
  // flow), the Selected tab falls back to the All-tab cache.
  departmentId?: string;
};

function InterventionsModalTableMultiselect({
  isOpen,
  onClose,
  onSelect,
  formRef,
  fieldName = 'interventions',
  departmentId,
}: InterventionsModalTableMultiselectProps) {
  const { t } = useTranslation();
  const [isFetchingAllIds, setIsFetchingAllIds] = useState(false);

  const selectedInterventions = useSelectedFormFieldIds(formRef, fieldName);

  const filterStateDefault = useMemo(
    () => ({
      [FilterFieldNames.Sites]: [],
      [FilterFieldNames.Equipment]: [],
    }),
    [],
  );

  const {
    activeTab,
    activeTabState,
    clearFilter,
    filterState,
    filterStateSelected,
    idsForSelectedTabQuery,
    isSelectedTabActive,
    orderingState,
    orderingStateSelected,
    paginationState,
    paginationStateSelected,
    searchState,
    searchStateSelected,
    setSelectedIdsForQuery,
  } = useModalTableModeManager({
    isOpen,
    mode: ModalTableMultiselectMode.WithTabs,
    selectedIds: selectedInterventions,
    filterStateDefault,
  });

  const { shouldRunAllQuery, handleAllTabQueryResult, getAllTabSnapshot } =
    useModalTableAllTabManager<Intervention>({
      isOpen,
      mode: ModalTableMultiselectMode.WithTabs,
      activeTab,
      isSelectedOnlyMode: false,
    });

  const searchQueryFilterSchema = [
    'name',
    'code',
    'assetName',
    'assetCode',
    'siteCode',
    'siteName',
    'siteAreaName',
    'siteAreaCode',
    'priority',
  ];

  const additionalFilters = useMemo(() => {
    if (!filterState) return undefined;
    return createAdditionalFilters(filterState[0] || {});
  }, [filterState]);

  // All tab query
  const {
    collection,
    loading: interventionsLoading,
    firstLoading: interventionsFirstLoading,
    totalCount,
    fetchIds,
  } = useInterventionsMultiselectQueryHook({
    searchQuery: searchState?.[0] || '',
    searchQueryFilterSchema,
    paginationParams: {
      ...(orderingState?.[0] || {}),
      ...(paginationState?.[0] || {}),
    },
    orderingParams: orderingState?.[0] || {},
    additionalFilters,
    skip: !shouldRunAllQuery,
  });

  useEffect(() => {
    handleAllTabQueryResult({
      collection,
      totalCount,
      loading: interventionsLoading,
      firstLoading: interventionsFirstLoading,
    });
  }, [
    collection,
    handleAllTabQueryResult,
    interventionsFirstLoading,
    interventionsLoading,
    totalCount,
  ]);

  const {
    collection: allTabCollection,
    totalCount: allTabTotalCount,
    firstLoading: allTabFirstLoading,
  } = getAllTabSnapshot({
    collection,
    totalCount,
    firstLoading: interventionsFirstLoading,
  });

  const additionalFiltersSelected = useMemo(() => {
    const base = createAdditionalFilters(filterStateSelected[0] || {});
    if (departmentId) {
      base.push({
        departmentsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: departmentId },
          filterGroup: 'departmentsId',
        },
      } as InterventionFilterInputObject);
    }
    return base;
  }, [filterStateSelected, departmentId]);

  // In edit mode, the Selected-tab query fetches by departmentsId. In create
  // mode (no departmentId), that query is skipped entirely.
  const {
    collection: collectionSelectedRaw,
    loading: interventionsLoadingSelected,
    firstLoading: interventionsFirstLoadingSelected,
    totalCount: totalCountSelectedRaw,
  } = useInterventionsMultiselectQueryHook({
    searchQuery: searchStateSelected[0],
    searchQueryFilterSchema,
    paginationParams: {
      ...orderingStateSelected[0],
      ...paginationStateSelected[0],
    },
    orderingParams: orderingStateSelected[0],
    additionalFilters: additionalFiltersSelected,
    skip:
      !isOpen ||
      !isSelectedTabActive ||
      !departmentId ||
      idsForSelectedTabQuery.length === 0,
    fetchPolicy: 'cache-first',
  });

  // Without a departmentId (create flow), fall back to filtering the
  // All-tab cache by the selected ids — InterventionFilterInputObject has no
  // `id`-IN filter to fetch an arbitrary Selected-tab id set directly.
  const collectionSelected = useMemo(() => {
    if (idsForSelectedTabQuery.length === 0) return [];
    if (departmentId) return collectionSelectedRaw;
    const selectedSet = new Set(idsForSelectedTabQuery);
    return allTabCollection.filter((item) => selectedSet.has(String(item.id)));
  }, [
    allTabCollection,
    collectionSelectedRaw,
    departmentId,
    idsForSelectedTabQuery,
  ]);
  const totalCountSelected = useMemo(() => {
    if (idsForSelectedTabQuery.length === 0) return 0;
    if (departmentId) return totalCountSelectedRaw;
    return collectionSelected.length;
  }, [
    collectionSelected.length,
    departmentId,
    idsForSelectedTabQuery.length,
    totalCountSelectedRaw,
  ]);

  // Sites query
  const [fetchSitesSearchListLazyQuery] = useSitesSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getSitesLoadOptions: GetLoadOptions = getLoadOptionsWithSelectedFilter(
    (filters) => getSitesLoadOptionsFn(fetchSitesSearchListLazyQuery, filters),
  );

  // Equipment/Assets query
  const [fetchAssetsSearchListLazyQuery] = useAssetsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getEquipmentLoadOptions: GetLoadOptions =
    getLoadOptionsWithSelectedFilter((filters) =>
      getAssetsLoadOptionsFn(
        fetchAssetsSearchListLazyQuery,
        undefined,
        undefined,
        filters,
      ),
    );

  const columns = getInterventionsColumns({ t });
  const filterFormFields = useFilterFields({
    getSitesLoadOptions,
    getEquipmentLoadOptions,
  });

  if (
    !activeTabState ||
    !searchState ||
    !paginationState ||
    !orderingState ||
    !filterState
  ) {
    return null;
  }

  return (
    <ModalTableMultiselect<Intervention>
      activeTabState={activeTabState}
      caption={t('intervention-multiselect')}
      columns={columns}
      data={allTabCollection}
      dataSelected={collectionSelected}
      fetchIds={fetchIds}
      filterFormFields={filterFormFields}
      filterState={filterState}
      filterStateSelected={filterStateSelected}
      filterStateDefault={filterStateDefault}
      firstLoading={allTabFirstLoading}
      firstLoadingSelected={interventionsFirstLoadingSelected}
      isOpen={isOpen}
      loading={shouldRunAllQuery ? interventionsLoading : false}
      loadingSelected={interventionsLoadingSelected || isFetchingAllIds}
      onClose={onClose}
      onIsFetchingAllIdsChange={setIsFetchingAllIds}
      onResetFilter={clearFilter}
      onSelect={onSelect}
      onSelectedIdsChange={setSelectedIdsForQuery}
      orderingState={orderingState}
      orderingStateSelected={orderingStateSelected}
      paginationState={paginationState}
      paginationStateSelected={paginationStateSelected}
      searchPleceholder={t('interventions-search-placeholder')}
      searchState={searchState}
      searchStateSelected={searchStateSelected}
      selected={selectedInterventions}
      totalEntries={allTabTotalCount}
      totalEntriesSelected={totalCountSelected}
    />
  );
}

export default memo(InterventionsModalTableMultiselect);
